import {
	OPERAND_TYPE_FUNCTION,
	OPERAND_TYPE_KEYWORD,
	OPERAND_TYPE_LIST,
	OPERAND_TYPE_VALUE,
	type Operand,
	type ListOperand,
} from '@atlaskit/jql-ast';
import type { IntlShape } from '@atlassian/jira-intl';
import type {
	SearchTemplate,
	SelectOption,
} from '@atlassian/jira-jql-builder-basic-picker/src/common/types.tsx';
import type { useHydratedValues$data } from '@atlassian/jira-relay/src/__generated__/useHydratedValues.graphql';
import { getFunctionValue } from '../get-function-value/index.tsx';
import { getKeywordValue } from '../get-keyword-value/index.tsx';
import { getValue } from '../get-value/index.tsx';

// Returns a minimal `SelectOption` which contains only the value. This is used
// during creation of `rawSelectedValues` in `./main.js`, and is necessary so
// that we can perform comparisons on the selected values before updated data
// is returned from the server.
const getRawValue = (value: string): SelectOption => ({
	optionType: 'option',
	label: '',
	value,
});

export const getListValue = ({
	listOperand,
	data,
	fieldJqlTerm,
	validOperandDisplayValues,
	intl,
	searchTemplate,
}: {
	listOperand: ListOperand;
	data: useHydratedValues$data;
	fieldJqlTerm: string;
	validOperandDisplayValues: string[] | undefined;
	intl: IntlShape;
	searchTemplate: SearchTemplate;
}): SelectOption[] =>
	listOperand.values.reduce<SelectOption[]>((acum: SelectOption[], value: Operand) => {
		if (value.operandType === OPERAND_TYPE_VALUE) {
			if (!data) {
				const rawValue = getRawValue(value.value);
				// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
				return rawValue ? [...acum, rawValue] : acum;
			}
			const hydratedValue = getValue(data, fieldJqlTerm, value.value, intl);
			// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
			return hydratedValue ? [...acum, hydratedValue] : acum;
		}

		if (value.operandType === OPERAND_TYPE_FUNCTION) {
			const hydratedValue = getFunctionValue(
				data,
				value.function.value,
				validOperandDisplayValues,
				intl,
				value.arguments,
				fieldJqlTerm,
			);

			// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
			return hydratedValue ? [...acum, hydratedValue] : acum;
		}

		if (value.operandType === OPERAND_TYPE_KEYWORD) {
			return [
				// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
				...acum,
				getKeywordValue(value.value, validOperandDisplayValues, intl, searchTemplate),
			];
		}

		if (value.operandType === OPERAND_TYPE_LIST) {
			return [
				// eslint-disable-next-line jira/js/no-reduce-accumulator-spread
				...acum,
				...getListValue({
					listOperand: value,
					data,
					fieldJqlTerm,
					validOperandDisplayValues,
					intl,
					searchTemplate,
				}),
			];
		}

		throw new Error('This is unexpected! Check if isQueryTooComplex is missing a check');
	}, []);
